import {Transition} from '@headlessui/react'
import React, {forwardRef, PropsWithChildren, useState} from 'react'

import {capitalizeFirstLetter, getPillTypeByMaterial} from '../../utils/utils'
import Button from '../buttons/Button'
import Flag from '../icons/Flag'
import Icon from '../icons/Icon'
import {CountryCode, countryMap} from '../icons/types'
import Pill from '../pills/Pill'

export type MaterialType = 'paper' | 'plastic';
export interface FacilityCardProps {
  selected: boolean;
  onSelect: () => void;
  onNotify?: () => void;
  name: string;
  description?: string;
  imageUrl?: string;
  facilityPageUrl?: string;
  city: string;
  countryCode: CountryCode;
  stock: number;
  insufficientStock?: boolean;
  material: MaterialType;
}

const defaultImageUrl: string =
  'https://images.unsplash.com/photo-1503694978374-8a2fa686963a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1738&q=80'

const FacilityCardOverlay = forwardRef<HTMLDivElement, PropsWithChildren<{}>>(
  ({children}, forwardRef) => {
    return (
      <div
        ref={forwardRef}
        className="absolute top-0 left-0 w-full h-full transform translate-y-full bg-black/70"
      >
        {children}
      </div>
    )
  }
)

const FacilityCard = ({
  selected,
  onSelect,
  onNotify,
  imageUrl,
  name,
  city,
  countryCode,
  stock,
  description,
  insufficientStock = false,
  facilityPageUrl,
  material
}: FacilityCardProps) => {
  const country = countryMap[countryCode]
  const [hovered, setHovered] = useState(false)

  const handleButtonClick = () => {
    return insufficientStock ? onNotify?.() : onSelect()
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`h-[304px] w-[272px] bg-kwota-neutral-100 flex overflow-hidden flex-col border rounded-xl group outline-none focus:border-kwota-primary relative ${
        selected
          ? 'ring ring-kwota-primary border-kwota-primary'
          : 'border-kwota-neutral-300'
      }`}
      tabIndex={0}
      onClick={() => !hovered && onSelect()}
    >
      <div className="h-[184px] w-full flex items-center justify-center overflow-hidden">
        <img
          src={imageUrl ?? defaultImageUrl}
          alt=""
          className="w-full h-full transition duration-500 ease-in-out transform group-hover:scale-110 "
        />
      </div>
      <div className="absolute top-3 left-3">
        <Pill text={capitalizeFirstLetter(material)} size="lg" type={getPillTypeByMaterial(material)} />
      </div>
      <div className="flex flex-col flex-grow px-3 pt-2 pb-3">
        <h3
          title={name}
          className="text-2xl font-semibold leading-8 text-left text-kwota-neutral-800 font-titillium line-clamp-1"
        >
          {name}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <Flag
            countryCode={countryCode as CountryCode}
            shape="rounded-square"
            size="sm"
            className="border border-kwota-neutral-300"
          />
          <p className="text-lg leading-6 font-titillium text-kwota-neutral-800">
            {city}, {country}
          </p>
        </div>
        <div className="flex items-end gap-2 mt-auto">
          <h3 className="text-2xl font-semibold leading-none text-kwota-neutral-800 font-titillium">
            {stock}
          </h3>
          <p className="text-lg leading-none font-titillium text-kwota-neutral-800">
            tonnes available
          </p>
        </div>
      </div>
      <Transition
        as={FacilityCardOverlay}
        show={hovered}
        enter="transition-transform duration-500"
        enterFrom="translate-y-full"
        enterTo="translate-y-0"
        leave="transition-transform duration-500"
        leaveFrom="translate-y-0"
        leaveTo="translate-y-full"
      >
        {insufficientStock &&
          <div className="flex items-center w-full gap-2 px-2 py-0.5 font-titillium bg-kwota-alert-100 text-kwota-alert-400">
            <Icon icon="error" className="fill-kwota-alert-400" size="sm" />
            Not enough stock
          </div>
        }
        <div className="px-3 py-2 space-y-4 text-center text-kwota-neutral-100 font-titillium">
          <p>{description}</p>
          {facilityPageUrl &&
            <a
              className="inline-block pb-1 font-semibold"
              href={facilityPageUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              <div className="flex items-center justify-center gap-2">
                See facility page
                <Icon
                  icon="open"
                  className="fill-kwota-neutral-100"
                  size="sm"
                />
              </div>
            </a>
          }
          {((insufficientStock && onNotify) || !insufficientStock) &&
            <Button
              fullWidth
              onClick={() => handleButtonClick()}
              text={insufficientStock ? 'Notify me when available' : 'Select'}
            />
          }
        </div>
      </Transition>
    </div>
  )
}

export default FacilityCard
